import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import TopHeader from "../Component/Header/TopHeader";
import {
  useApproveRejectupdateChapterMutation,
  useGetChapterBasedOnChapterUrlMutation,
} from "../../src/app/services/chapterServices";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import "../style/css/ViewIndividualChapter.css";
import "react-multi-carousel/lib/styles.css";
import ViewForum from "./Admin/ViewForum";
import Spinner from "../Component/spinner/spinner";
import Event from "../Component/Portal/Event";
import Gallery from "../Component/Portal/Gallery";
import { toast } from "react-toastify";
import userLogo from "../../src/assets/images/login/userLogo.jpg";
import ImportantAnnouncementTable from "./Admin/ImportAnnocement/ImportantAnnouncementTable";
import { Salutation } from "../constant/constant";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import placeholderImage from "../assets/images/placeholder/placeholder.png";
import { InputAdornment, TextField, Typography, Modal } from "@mui/material";
import HtmlDiff from 'htmldiff-js';
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { selectCurrentScope } from "../app/auth/authSlice";
import { usePaginationchapterRewardMutation } from "../app/services/chapterRewardsService";
import { Phone, Mail, LocationOn, AccountCircleOutlined } from "@mui/icons-material";

/**
 * Quill (PrimeReact Editor) saves BOTH bullet and ordered lists as <ol> tags,
 * distinguishing them only via a `data-list="bullet" | "ordered"` attribute
 * on each <li>. Quill's own ql-editor CSS normally converts that attribute
 * into the correct marker, but since we render the saved HTML here via
 * dangerouslySetInnerHTML (outside the actual <Editor/>), none of that CSS
 * is present -> the browser falls back to plain numbered <ol> markers for
 * everything, including bullet lists.
 *
 * This scoped, self-contained style block replicates Quill's own list
 * rendering rules so bullet lists show as bullets and ordered lists keep
 * their numbering, without touching any external CSS files or existing
 * design/layout.
 */
const QuillListFix = () => (
  <style>{`
        .chapter-description-html ol {
            padding-left: 1.5em;
            margin: 0 0 0.5em 0;
            list-style: none !important;
            counter-reset: chapter-ordered-counter;
        }
        .chapter-description-html ol > li {
            position: relative;
            list-style: none !important;
        }
        .chapter-description-html ol > li::before {
            content: none;
        }
        .chapter-description-html ol > li[data-list="bullet"]::before {
            content: "•";
            position: absolute;
            left: -1.2em;
        }
        .chapter-description-html ol > li[data-list="ordered"] {
            counter-increment: chapter-ordered-counter;
        }
        .chapter-description-html ol > li[data-list="ordered"]::before {
            content: counter(chapter-ordered-counter) ". ";
            position: absolute;
            left: -1.5em;
        }
        .chapter-description-html ul {
            padding-left: 1.5em;
            margin: 0 0 0.5em 0;
        }
        .chapter-description-html ul > li {
            list-style: disc;
        }
    `}</style>
);

// ─── Constants ────────────────────────────────────────────────────────────────

const MODAL_STYLE = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  backgroundColor: "white",
  boxShadow: 24,
  borderRadius: "5px",
  padding: "20px",
};

/** Shared MUI Tabs container sx — matches ViewSigDetail design */
const TABS_SX = {
  mx: "auto",
  width: "100%",
  borderRadius: "8px",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(97,31,30,0.18)",
  backgroundColor: "#ffffff",
  border: "2px solid #611f1e",
  p: "4px",
  "& .MuiTabs-indicator": {
    backgroundColor: "#611f1e",
    height: "100%",
    borderRadius: "6px",
    zIndex: 1,
  },
};

/** Tab sx factory — active tab gets white text, inactive gets theme colour */
const tabSx = (isActive) => ({
  fontWeight: 700,
  fontSize: "0.9rem",
  transition: "all 0.3s ease",
  zIndex: 2,
  borderRadius: "6px",
  mx: "2px",
  minHeight: "44px",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: isActive ? "#ffffff !important" : "#611f1e !important",
});

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const getSalutationLabel = (id) => {
  if (id == null) return "";
  const found = Salutation.find((s) => s.id === id);
  return found?.Name ? `${found.Name}.` : "";
};

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-In", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    : null;

const isCompletelyDifferent = (oldHtml, newHtml) => {
  if (!oldHtml || !newHtml) return true;
  const oldText = String(oldHtml).replace(/<[^>]*>?/gm, '').trim();
  const newText = String(newHtml).replace(/<[^>]*>?/gm, '').trim();

  const stopWords = new Set(['the', 'and', 'is', 'in', 'to', 'of', 'a', 'for', 'on', 'with', 'as', 'by']);
  const getWords = (text) => text.toLowerCase().split(/\s+/).filter(w => w.length > 2 && !stopWords.has(w));

  const oldWords = getWords(oldText);
  const newWords = getWords(newText);

  if (oldWords.length === 0 || newWords.length === 0) return true;

  const oldWordsSet = new Set(oldWords);
  let matchCount = 0;
  for (const w of newWords) {
    if (oldWordsSet.has(w)) matchCount++;
  }

  const maxLen = Math.max(oldWords.length, newWords.length);
  if (maxLen === 0) return false;

  return (matchCount / maxLen) < 0.15;
};

/** Renders `text`, and if updated, highlights the entire text. */
const HighlightText = ({ text, highlighted, originalText }) => {
  if (!highlighted) return <>{text}</>;
  return <span className="bg-warning">{text}</span>;
};

// ─── Chapter Office Bearer Card ───────────────────────────────────────────────
/**
 * Premium vertical card matching the ViewSigDetail RoleBearerCard design.
 * Accepts a `highlight` map so any field can be yellow-highlighted when changed.
 */
const ChapterRoleBearerCard = ({
  salutationLabel,
  name,
  role,
  institution,
  designation,
  mobile,
  email,
  address,
  pincode,
  image,
  highlight = {},
  originalTextMap = {},
  isAdmin,
  showUserId,
  userId,
  isNew,
  isRemoved,
  dimmed,
}) => (
  <div
    className={`position-relative ${dimmed ? "opacity-50" : ""}`}
    style={{
      background: "#ffffff",
      borderRadius: "16px",
      padding: "30px 24px",
      border: "1px solid #f0eae0",
      boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      height: "100%",
      transition: "transform 0.25s ease, box-shadow 0.25s ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-5px)";
      e.currentTarget.style.boxShadow = "0 12px 30px rgba(97,31,30,0.12)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.05)";
    }}
  >
    {isNew && (
      <span className="position-absolute poppins-regular top-0 end-0 badge rounded me-3 mt-3 bg-success">
        New<span className="visually-hidden">New</span>
      </span>
    )}
    {isRemoved && (
      <span
        style={{ opacity: "1 !important" }}
        className="position-absolute poppins-regular top-0 end-0 badge rounded me-3 mt-3 bg-danger"
      >
        Removed<span className="visually-hidden">Removed</span>
      </span>
    )}

    {/* Profile image with gold border ring */}
    <div className={highlight?.image ? "bg-warning rounded-circle p-1" : ""}>
      <div
        style={{
          width: "120px",
          height: "120px",
          borderRadius: "50%",
          overflow: "hidden",
          border: highlight?.image ? "none" : "4px solid #d4af37",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f8f8f8",
        }}
      >
        <img
          src={image || userLogo}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          alt={name}
        />
      </div>
    </div>

    {/* Role badge */}
    <div
      style={{
        backgroundColor: "#611f1e",
        color: "#ffffff",
        fontSize: "0.75rem",
        fontWeight: "700",
        padding: "4px 14px",
        borderRadius: "50px",
        textTransform: "capitalize",
        marginBottom: "12px",
        letterSpacing: "0.5px",
      }}
    >
      <HighlightText text={role} highlighted={highlight.role} originalText={originalTextMap?.role} />
    </div>

    {/* Name */}
    <h4
      style={{
        fontSize: "1.1rem",
        fontWeight: "800",
        color: "#0f3d1e",
        margin: "0 0 4px 0",
        fontFamily: "Georgia, serif",
      }}
    >
      {salutationLabel && (
        <HighlightText text={`${salutationLabel} `} highlighted={highlight.salutation} originalText={originalTextMap?.salutation ? `${originalTextMap.salutation} ` : undefined} />
      )}
      <HighlightText text={name} highlighted={highlight.name} originalText={originalTextMap?.name} />
    </h4>

    {/* Institution / Designation subtitle */}
    {(institution || designation) && (
      <p style={{ fontSize: "0.8rem", color: "#666", margin: "0 0 12px 0", fontStyle: "italic" }}>
        <HighlightText text={institution} highlighted={highlight.institution} originalText={originalTextMap?.institution} />
        {institution && designation && " "}
        {designation && (
          <>
            (<HighlightText text={designation} highlighted={highlight.designation} originalText={originalTextMap?.designation} />)
          </>
        )}
      </p>
    )}

    {/* Separator */}
    <div style={{ width: "40px", height: "2px", backgroundColor: "#e2d6b5", marginBottom: "16px" }} />

    {/* Contact details */}
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        textAlign: "left",
        fontSize: "0.85rem",
      }}
    >
      {mobile && (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#444" }}>
          <Phone style={{ fontSize: "1.05rem", color: "#611f1e" }} />
          <span>
            <HighlightText text={mobile} highlighted={highlight.mobile} originalText={originalTextMap?.mobile} />
          </span>
        </div>
      )}
      {email && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            color: "#444",
            wordBreak: "break-all",
          }}
        >
          <Mail style={{ fontSize: "1.05rem", color: "#611f1e" }} />
          <span>
            <HighlightText text={email} highlighted={highlight.email} originalText={originalTextMap?.email} />
          </span>
        </div>
      )}
      {(address || pincode) && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", color: "#444" }}>
          <LocationOn style={{ fontSize: "1.05rem", color: "#611f1e", marginTop: "2px" }} />
          <span>
            <HighlightText text={address} highlighted={highlight.address} originalText={originalTextMap?.address} />
            {address && pincode && " - "}
            <HighlightText text={pincode} highlighted={highlight.pincode} originalText={originalTextMap?.pincode} />
          </span>
        </div>
      )}
      {showUserId && userId && (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#888" }}>
          <AccountCircleOutlined style={{ fontSize: "1.05rem" }} />
          <span>ID: {userId}</span>
        </div>
      )}
    </div>

    {isAdmin && (
      <span className="position-absolute top-0 end-0 badge px-0 py-0 me-2 mt-2">
        <AdminPanelSettingsIcon className="menus-green-color" style={{ fontSize: "1.3rem" }} />
      </span>
    )}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

function ViewIndividualChapter() {
  const { chapterId } = useParams();
  const [getChapterDetailsById] = useGetChapterBasedOnChapterUrlMutation();
  const [getChapterData, setGetChapterData] = useState([]);
  const [displayedOffice, setDisplayedOffice] = useState(2);
  const [lengthOfRole, setLengthOfRole] = useState(0);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [updateDetails, setUpdateDetails] = useState(null);
  const [updatedKeys, setUpdatedKeys] = useState([]);
  const location = useLocation();
  const currentUrl = location.pathname;
  const isPending = currentUrl.includes("pending");
  const [loading, setLoading] = useState(false);
  const [approveRejectUpdate] = useApproveRejectupdateChapterMutation();
  const [updatedRoleDetails, setUpdatedRoleDetails] = useState([]);
  const [open, setOpen] = useState(false);
  const memberScope = useSelector(selectCurrentScope);
  const isSuperAdmin = memberScope === "PORTAL_SUPER_ADMIN";
  const [getAllPaginationchapterReward] = usePaginationchapterRewardMutation();
  const [allChapterRewards, setAllChapterReward] = useState([]);

  const {
    register,
    formState: { errors },
    watch,
    reset,
    handleSubmit,
  } = useForm({ defaultValues: { rejectionReason: "" } });

  const handleOpen = () => setOpen(true);
  const handleClose = () => { setOpen(false); reset(); };
  const handleTabChange = (_, tabIndex) => setCurrentTabIndex(tabIndex);
  const loadMoreOffice = () => setDisplayedOffice((prev) => prev + 2);

  const showRewards = (id) => {
    const found = allChapterRewards.find((d) => d?._id === id);
    return found?.uploadImage || null;
  };

  // ── Data fetching ─────────────────────────────────────────────────────────

  const fetchChapterDetails = useCallback(() => {
    if (!chapterId) return;
    setLoading(true);
    getChapterDetailsById({ chapterUrl: chapterId })
      .then((res) => {
        setLoading(false);
        if (!res.data?.status) return;
        const chapterData = res.data.data;
        setGetChapterData(chapterData);

        if (isPending) {
          const original = chapterData?.roleDetails ?? [];
          const update = chapterData?.pendingChanges?.roleDetails ?? [];
          const merged = [
            ...original.map((item) => {
              const u = update.find((u) => u.email === item.email);
              return u ? { ...item, ...u, isNew: false } : { ...item, isNew: false };
            }),
            ...update
              .filter((u) => !original.some((o) => o.email === u.email))
              .map((item) => ({ ...item, isNew: true })),
          ];
          setUpdatedRoleDetails(merged);
          setDisplayedOffice(merged.length);
          setUpdateDetails(chapterData?.pendingChanges);
          setUpdatedKeys(Object.keys(chapterData?.pendingChanges ?? {}));
          setLengthOfRole(currentUrl.includes('pending') ? chapterData?.pendingChanges?.roleDetails ? chapterData?.pendingChanges?.roleDetails.length : chapterData?.roleDetails.length : chapterData?.roleDetails.length);

        } else {
          setLengthOfRole(chapterData?.roleDetails?.length);
        }
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err.response?.data?.message || "An error occurred");
      });
  }, [chapterId, isPending, getChapterDetailsById, currentUrl]);

  useEffect(() => { fetchChapterDetails(); }, [fetchChapterDetails]);

  console.log(updateDetails, 'updateDetails')
  const fetchData = useCallback(() => {
    setLoading(true);
    getAllPaginationchapterReward({ page: "", itemsPerPage: "" })
      .then((res) => {
        setLoading(false);
        setAllChapterReward(res?.data?.data?.chapterRewardDetails || []);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching rewards list:", error);
      });
  }, [getAllPaginationchapterReward]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Approve / Reject ──────────────────────────────────────────────────────

  const approveReject = (type) => {
    setLoading(true);
    approveRejectUpdate({
      chapterId: getChapterData?._id,
      type,
      rejectionReason: watch("rejectionReason"),
    }).then((res) => {
      setLoading(false);
      if (res?.data?.status) {
        window?.history.back();
        res?.data?.message === "Updated Chapter Approved Successfully!"
          ? toast.success(res.data.message)
          : toast.error(res.data.message);
      }
    });
  };

  // ── Card prop builders ────────────────────────────────────────────────────

  /**
   * Builds ChapterRoleBearerCard props for a role in the `updatedRoleDetails`
   * list (used on pending view when updateDetails.roleDetails exists).
   * Highlights fields that changed vs the original data.
   */
  const buildUpdatedCardProps = (data, index) => {
    const originalRole =
      getChapterData?.roleDetails?.find((r) => r.email === data?.email) ??
      getChapterData?.roleDetails?.[index];
    const isRemoved =
      (updateDetails?.removedAdminEmail?.length ?? 0) > 0 &&
      updateDetails.removedAdminEmail.includes(data.email);

    // New or removed roles: show values as-is, no highlighting
    if (data.isNew || isRemoved) {
      return {
        salutationLabel: getSalutationLabel(data?.salutation ?? originalRole?.salutation),
        name: data?.name || originalRole?.name,
        role: data?.role || originalRole?.role,
        institution: data?.institution || originalRole?.institution,
        designation: data?.designation || originalRole?.designation,
        mobile: data?.mobile || originalRole?.mobile,
        email: data?.email || originalRole?.email,
        address: data?.address || originalRole?.address,
        pincode: data?.pincode || originalRole?.pincode,
        image: data?.roleImageUrl !== undefined ? data?.roleImageUrl : originalRole?.roleImageUrl,
        highlight: {},
        isAdmin: originalRole?.isAdmin,
        isNew: data?.isNew,
        isRemoved,
        dimmed: isRemoved,
        userId: data?.userId || originalRole?.userId,
      };
    }

    // Changed role: highlight differing fields
    const field = (key) => ({
      value: originalRole?.[key] !== data?.[key] ? data?.[key] : originalRole?.[key],
      highlighted: originalRole?.[key] !== data?.[key],
    });
    const salutationChanged = originalRole?.salutation !== data?.salutation;
    const imageChanged =
      originalRole?.roleImageUrl !== data?.roleImageUrl && data?.roleImageUrl !== undefined;

    return {
      salutationLabel: salutationChanged
        ? getSalutationLabel(data?.salutation)
        : getSalutationLabel(originalRole?.salutation),
      name: field("name").value,
      role: field("role").value,
      institution: field("institution").value,
      designation: field("designation").value,
      mobile: field("mobile").value,
      email: field("email").value,
      address: field("address").value,
      pincode: field("pincode").value,
      image: imageChanged ? (data?.roleImageUrl === "" ? placeholderImage : data?.roleImageUrl) : originalRole?.roleImageUrl,
      highlight: {
        salutation: salutationChanged,
        name: field("name").highlighted,
        role: field("role").highlighted,
        institution: field("institution").highlighted,
        designation: field("designation").highlighted,
        mobile: field("mobile").highlighted,
        email: field("email").highlighted,
        address: field("address").highlighted,
        pincode: field("pincode").highlighted,
        image: imageChanged,
      },
      originalTextMap: {
        salutation: getSalutationLabel(originalRole?.salutation),
        name: originalRole?.name,
        role: originalRole?.role,
        institution: originalRole?.institution,
        designation: originalRole?.designation,
        mobile: originalRole?.mobile,
        email: originalRole?.email,
        address: originalRole?.address,
        pincode: originalRole?.pincode,
      },
      isAdmin: originalRole?.isAdmin,
      isRemoved,
      dimmed: isRemoved,
      userId: data?.userId || originalRole?.userId,
    };
  };

  /**
   * Builds ChapterRoleBearerCard props for a role in the original
   * `getChapterData.roleDetails` list when `updateDetails` is present
   * (highlights pending updates from updateDetails.roleDetails[index]).
   */
  const buildOriginalCardProps = (data, index) => {
    const updatedRole = updateDetails?.roleDetails?.[index];
    const isRemoved = updateDetails?.removedAdminEmail?.includes(data?.email);
    const field = (key) => ({
      value: updatedRole?.[key] ?? data?.[key],
      highlighted: updatedRole?.[key] != null,
    });
    const salutationField = field("salutation");

    return {
      salutationLabel: salutationField.highlighted
        ? getSalutationLabel(salutationField.value)
        : getSalutationLabel(data?.salutation),
      name: field("name").value,
      role: field("role").value,
      institution: field("institution").value,
      designation: field("designation").value,
      mobile: field("mobile").value,
      email: field("email").value,
      address: field("address").value,
      pincode: field("pincode").value,
      image: updatedRole?.roleImageUrl !== undefined ? (updatedRole.roleImageUrl === "" ? placeholderImage : updatedRole.roleImageUrl) : data?.roleImageUrl,
      highlight: {
        salutation: salutationField.highlighted,
        name: field("name").highlighted,
        role: field("role").highlighted,
        institution: field("institution").highlighted,
        designation: field("designation").highlighted,
        mobile: field("mobile").highlighted,
        email: field("email").highlighted,
        address: field("address").highlighted,
        pincode: field("pincode").highlighted,
        image: updatedRole?.roleImageUrl !== undefined && updatedRole?.roleImageUrl !== data?.roleImageUrl,
      },
      originalTextMap: {
        salutation: getSalutationLabel(data?.salutation),
        name: data?.name,
        role: data?.role,
        institution: data?.institution,
        designation: data?.designation,
        mobile: data?.mobile,
        email: data?.email,
        address: data?.address,
        pincode: data?.pincode,
      },
      isAdmin: data?.isAdmin,
      isRemoved,
      dimmed: isRemoved,
      userId: data?.userId,
    };
  };
  // ── Derived display values ────────────────────────────────────────────────

  const establishedDate =
    updatedKeys.includes("establishedOn") &&
      getChapterData?.establishedOn?.split("T")[0] !== updateDetails?.establishedOn ? (
      <span className="bg-warning">{formatDate(updateDetails?.establishedOn)}</span>
    ) : (
      formatDate(getChapterData?.establishedOn)
    );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <QuillListFix />
      <div className="page-containter">
        <TopHeader />
        <div className="pt-5">
          <div className="chapter-heading">
            <h2 className="text-center title-font text-light py-3 mt-1">View Chapter Details</h2>
          </div>
        </div>

        <div className="p-md-5 p-3 overallContactPadding">
          <div className="text-end mb-3">
            <Button onClick={() => window.history.back()} variant="contained" className="menus-color">
              Back
            </Button>
          </div>

          {!isPending && (
            <Grid
              container
              spacing={2}
              style={{
                maxWidth: "900px",
                margin: "0 auto",
                position: "sticky",
                top: 0,
                zIndex: 999,
                marginBottom: "24px",
              }}
            >
              <Tabs
                value={currentTabIndex}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={TABS_SX}
              >
                {["About", "Events", "Forums", "Gallery", "Announcements"].map((label, idx) => (
                  <Tab
                    key={label}
                    label={label}
                    className="title-font"
                    sx={tabSx(currentTabIndex === idx)}
                  />
                ))}
              </Tabs>
            </Grid>
          )}

          <div>
            {currentTabIndex === 0 && (
              <>
                {loading ? (
                  <Spinner />
                ) : (
                  <div className="container-fluid chapterDetails">

                    {/* Chapter title */}
                    <h1
                      style={{
                        color: "#611f1e",
                        fontFamily: "Georgia, serif",
                        fontWeight: "800",
                        fontSize: "2.2rem",
                        lineHeight: "1.2",
                        margin: "0 0 24px 0",
                        textAlign: "center",
                      }}
                    >
                      {updatedKeys.includes("title") ? (
                        <HighlightText text={updateDetails?.title} highlighted={true} originalText={getChapterData?.title} />
                      ) : (
                        getChapterData?.title
                      )}
                    </h1>

                    {/* Chapter info card */}
                    <div
                      style={{
                        background: "#fff9f5",
                        border: "1px solid #f0eae0",
                        borderRadius: "12px",
                        padding: "20px 24px",
                        maxWidth: "680px",
                        margin: "0 auto 40px",
                      }}
                    >
                      <div className="row align-items-center">
                        {/* Logo */}
                        <div className="col-md-4 d-flex justify-content-center mb-3 mb-md-0">
                          {updatedKeys.includes("imageUrl") ? (
                            <span className="bg-warning d-inline-block rounded p-1">
                              <img
                                src={updateDetails?.imageUrl}
                                alt="Chapter logo"
                                style={{ width: "120px", height: "120px", objectFit: "contain", borderRadius: "8px" }}
                              />
                            </span>
                          ) : getChapterData?.imageUrl ? (
                            <img
                              src={getChapterData?.imageUrl}
                              alt="Chapter logo"
                              style={{ width: "120px", height: "120px", objectFit: "contain", borderRadius: "8px" }}
                            />
                          ) : (
                            <img
                              src={placeholderImage}
                              alt="Chapter logo"
                              style={{ width: "120px", height: "120px", objectFit: "cover", borderRadius: "50%" }}
                            />
                          )}
                        </div>

                        {/* Meta details */}
                        <div className="col-md-8">
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                              fontSize: "0.92rem",
                              color: "#444",
                            }}
                          >
                            {establishedDate && (
                              <div>
                                <span style={{ fontWeight: 700, color: "#611f1e" }}>Established On: </span>
                                {establishedDate}
                              </div>
                            )}
                            {(getChapterData?.district || getChapterData?.state) && (
                              <div>
                                <span style={{ fontWeight: 700, color: "#611f1e" }}>District &amp; State: </span>
                                {updatedKeys.includes("district") ? (
                                  <HighlightText text={updateDetails?.district} highlighted={true} originalText={getChapterData?.district} />
                                ) : (
                                  getChapterData?.district
                                )}
                                {", "}
                                {updatedKeys.includes("state") ? (
                                  <HighlightText text={updateDetails?.state} highlighted={true} originalText={getChapterData?.state} />
                                ) : (
                                  getChapterData?.state
                                )}
                              </div>
                            )}
                            {getChapterData?.country && (
                              <div>
                                <span style={{ fontWeight: 700, color: "#611f1e" }}>Country: </span>
                                {updatedKeys.includes("country") ? (
                                  <HighlightText text={updateDetails?.country} highlighted={true} originalText={getChapterData?.country} />
                                ) : (
                                  getChapterData?.country
                                )}
                              </div>
                            )}
                          </div>
                          {/* Reward badges */}
                          {getChapterData?.addReward?.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mt-3">
                              {getChapterData?.addReward?.map((reward, i) => {
                                const img = showRewards(reward);
                                return img ? (
                                  <span key={i} className="badge text-bg-error p-0">
                                    <img
                                      src={img}
                                      alt={`Reward ${i + 1}`}
                                      className="show-bage-chapter-rewards rounded-circle"
                                    />
                                  </span>
                                ) : (
                                  <span key={i} className="badge text-bg-warning">
                                    Will Be Added Shortly
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    {(getChapterData?.description || updateDetails?.description) && (
                      <div className="chapter-description-html">
                        {updatedKeys.includes("description") ? (
                          isCompletelyDifferent(getChapterData?.description, updateDetails?.description) ? (
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <div style={{ backgroundColor: '#fff5f5', border: '1px solid #ffcdd2', borderRadius: '8px', padding: '16px', height: '100%' }}>
                                  <Typography variant="subtitle2" sx={{ color: '#d32f2f', fontWeight: 'bold', mb: 1 }}>
                                    Previous Version
                                  </Typography>
                                  <div
                                    style={{ opacity: 0.8 }}
                                    dangerouslySetInnerHTML={{ __html: getChapterData?.description || "<em>No previous description</em>" }}
                                  />
                                </div>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <div style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '8px', padding: '16px', height: '100%' }}>
                                  <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 1 }}>
                                    Updated Version
                                  </Typography>
                                  <div
                                    dangerouslySetInnerHTML={{ __html: updateDetails?.description || "<em>Description cleared</em>" }}
                                  />
                                </div>
                              </Grid>
                            </Grid>
                          ) : (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: HtmlDiff.execute(
                                  getChapterData?.description || "",
                                  updateDetails?.description || ""
                                )
                              }}
                            />
                          )
                        ) : (
                          <div
                            dangerouslySetInnerHTML={{ __html: getChapterData?.description }}
                          />
                        )}
                      </div>
                    )}

                    {/* Office Bearers section heading */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "8px",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: "2px",
                          background: "linear-gradient(to right, #611f1e, transparent)",
                        }}
                      />
                      <h3
                        style={{
                          color: "#611f1e",
                          fontWeight: 800,
                          fontSize: "1.2rem",
                          margin: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Office Bearers
                      </h3>
                      <div
                        style={{
                          flex: 1,
                          height: "2px",
                          background: "linear-gradient(to left, #611f1e, transparent)",
                        }}
                      />
                    </div>
                    {lengthOfRole === 0 && (
                      <h1 className="text-secondary-new alata-bold text-capitalize">
                        Will Be Added Shortly
                      </h1>
                    )}

                    {/* Office Bearer Cards */}
                    <div className="row mb-5">
                      <div className="col-md-1" />
                      <div className="col-md-10">
                        <div className="row">
                          {updateDetails?.roleDetails
                            ? updatedRoleDetails.slice(0, displayedOffice).map((data, index) => (
                              <div
                                className="col-lg-6 col-md-6 col-sm-12 mt-5"
                                key={data.email || index}
                              >
                                <ChapterRoleBearerCard
                                  {...buildUpdatedCardProps(data, index)}
                                  showUserId={isSuperAdmin}
                                />
                                {console.log(buildUpdatedCardProps(data, index), 'data, index) inside the upside')}
                              </div>

                            ))
                            : (getChapterData?.roleDetails || []).slice(0, displayedOffice).map((data, index) => (
                              <div
                                className="col-lg-6 col-md-6 col-sm-12 mt-5"
                                key={data.email || index}
                              >
                                <ChapterRoleBearerCard
                                  {...buildOriginalCardProps(data, index)}
                                  showUserId={isSuperAdmin}
                                />
                                {console.log(buildOriginalCardProps(data, index), 'data, index)')}
                              </div>
                            ))}
                        </div>
                      </div>
                      <div className="col-md-1" />
                    </div>

                    {lengthOfRole > displayedOffice && (
                      <div className="w-100 text-end mb-3">
                        <Button variant="contained" className="menus-color" onClick={loadMoreOffice}>
                          Load More
                        </Button>
                      </div>
                    )}

                    {/* Approve / Reject buttons (pending + super admin) */}
                    {isPending && isSuperAdmin && (
                      <div className="w-100 text-end mb-3">
                        <Button
                          variant="contained"
                          color="error"
                          className="rounded-0 me-3"
                          onClick={handleOpen}
                        >
                          Reject
                        </Button>
                        <Button
                          variant="contained"
                          className="menus-color"
                          onClick={() => approveReject(0)}
                        >
                          Approve
                        </Button>
                      </div>
                    )}

                    {/* Reject reason modal */}
                    <Modal
                      open={open}
                      onClose={handleClose}
                      aria-labelledby="modal-modal-title"
                      aria-describedby="modal-modal-description"
                    >
                      <div style={MODAL_STYLE} className="d-flex align-items-center flex-column gap-3">
                        <form className="w-100" onSubmit={handleSubmit(() => approveReject(1))}>
                          <Typography
                            component="h2"
                            className="text-center fs-4 fw-bold text-danger mb-2"
                          >
                            Reject Chapter Updates
                          </Typography>
                          <TextField
                            inputProps={{ maxLength: "300" }}
                            fullWidth
                            multiline
                            rows={5}
                            label={
                              <Typography className="text-dark">
                                Reason for Rejection{" "}
                                <span style={{ color: "red" }}>*</span>
                              </Typography>
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {300 - (watch("rejectionReason")?.length || 0)}
                                </InputAdornment>
                              ),
                            }}
                            error={!!errors?.rejectionReason}
                            helperText={errors?.rejectionReason?.message}
                            {...register("rejectionReason", {
                              required: "Please enter reason for rejection",
                            })}
                          />
                          <div className="mt-3 mx-auto d-flex justify-content-center align-items-center">
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={handleClose}
                              style={{ marginRight: "10px" }}
                            >
                              Cancel
                            </Button>
                            <Button
                              className="menus-color"
                              variant="contained"
                              color="success"
                              type="submit"
                            >
                              Submit
                            </Button>
                          </div>
                        </form>
                      </div>
                    </Modal>
                  </div>
                )}
              </>
            )}

            {currentTabIndex === 1 && <Event chapterID={getChapterData?._id} />}
            {currentTabIndex === 2 && <ViewForum />}
            {currentTabIndex === 3 && <Gallery />}
            {currentTabIndex === 4 && <ImportantAnnouncementTable chapterID={getChapterData?._id} />}
          </div>
        </div>
      </div>
    </>
  );
}

export default ViewIndividualChapter;